const tones = {
    primary: {
        accent: '#2563eb',
        soft: '#dbeafe',
        glow: 'rgba(37, 99, 235, 0.2)'
    },
    secondary: {
        accent: '#10b981',
        soft: '#d1fae5',
        glow: 'rgba(16, 185, 129, 0.2)'
    },
    accent: {
        accent: '#8b5cf6',
        soft: '#ede9fe',
        glow: 'rgba(139, 92, 246, 0.2)'
    },
    warning: {
        accent: '#f59e0b',
        soft: '#fef3c7',
        glow: 'rgba(245, 158, 11, 0.2)'
    },
    error: {
        accent: '#ef4444',
        soft: '#fee2e2',
        glow: 'rgba(239, 68, 68, 0.22)'
    },
    info: {
        accent: '#3b82f6',
        soft: '#dbeafe',
        glow: 'rgba(59, 130, 246, 0.2)'
    },
    neutral: {
        accent: '#64748b',
        soft: '#e2e8f0',
        glow: 'rgba(100, 116, 139, 0.18)'
    }
};

const colorsBase = {
    ink: '#0f172a',
    muted: '#64748b',
    white: '#ffffff',
    green: '#10b981',
    red: '#ef4444',
    yellow: '#f59e0b'
};

const Frame = ({ children }) => (
    <svg
        viewBox="0 0 180 128"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
    >
        {children}
    </svg>
);

const InsoleShape = ({ x, y, scale = 1, mirror = false, fill, stroke }) => (
    <path
        d="M0 0c-8 0-14 6-14 15 0 4 1 8 3 11l7 15c2 4 3 7 3 10 0 9 7 15 16 15 10 0 17-8 17-18 0-4-1-8-3-12l-5-10c-2-4-3-8-3-12C21 7 12 0 0 0Z"
        transform={`translate(${x} ${y}) scale(${mirror ? -scale : scale} ${scale})`}
        fill={fill}
        stroke={stroke}
        strokeWidth="3"
        strokeLinejoin="round"
    />
);

const PainSpark = ({ x, y, color }) => (
    <g transform={`translate(${x} ${y})`} stroke={color} strokeLinecap="round" strokeWidth="3">
        <path d="M0-10 3-2 12 0 3 2 0 10-3 2-12 0-3-2Z" fill={color} />
    </g>
);

const SupportArt = ({ accent, soft, ink, green }) => (
    <>
        <InsoleShape x="72" y="30" fill={soft} stroke={accent} />
        <InsoleShape x="108" y="30" mirror fill={soft} stroke={accent} />
        <path d="M60 84c10 7 20 10 30 10s20-3 30-10" fill="none" stroke={green} strokeWidth="4" strokeLinecap="round" />
        <circle cx="58" cy="42" r="7" fill="#ffffff" stroke={accent} strokeWidth="3" />
        <path d="M54 42h8M58 38v8" stroke={accent} strokeWidth="3" strokeLinecap="round" />
        <path d="M112 42h8l3-5 4 10 4-8 3 3h8" fill="none" stroke={ink} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </>
);

const FlatFeetArt = ({ accent, soft, yellow }) => (
    <>
        <InsoleShape x="72" y="28" fill={soft} stroke={accent} />
        <InsoleShape x="108" y="28" mirror fill={soft} stroke={accent} />
        <path d="M52 86h76" fill="none" stroke={accent} strokeWidth="4" strokeLinecap="round" />
        <path d="M64 74c6-3 14-5 26-5s20 2 26 5" fill="none" stroke={yellow} strokeWidth="3" strokeLinecap="round" strokeDasharray="4 5" />
    </>
);

const HeelSpurArt = ({ accent, soft, yellow }) => (
    <>
        <path
            d="M54 84c0-18 13-33 31-35 13-2 28 6 37 18l11 14-11 8-9-11c-4 11-14 19-27 19H62c-5 0-8-4-8-9Z"
            fill={soft}
            stroke={accent}
            strokeWidth="3"
            strokeLinejoin="round"
        />
        <PainSpark x="53" y="86" color={yellow} />
    </>
);

const DiabeticUlcersArt = ({ accent, soft, red, green }) => (
    <>
        <InsoleShape x="84" y="28" fill={soft} stroke={accent} />
        <path d="M110 48c0-8 6-14 14-14s14 6 14 14v10c0 11-8 18-14 23-6-5-14-12-14-23V48Z" fill="#ffffff" stroke={green} strokeWidth="3" strokeLinejoin="round" />
        <path d="M118 56h12M124 50v12" stroke={green} strokeWidth="3" strokeLinecap="round" />
        <PainSpark x="78" y="78" color={red} />
    </>
);

const ScoliosisArt = ({ accent, soft, ink }) => (
    <>
        <path d="M72 28c10 2 16 10 16 20 0 8-5 13-5 21 0 9 8 15 20 18" fill="none" stroke={accent} strokeWidth="5" strokeLinecap="round" />
        {[
            [72, 28],
            [76, 37],
            [80, 46],
            [81, 56],
            [78, 66],
            [81, 76],
            [90, 84],
            [101, 90]
        ].map(([cx, cy], index) => (
            <circle key={`${cx}-${cy}-${index}`} cx={cx} cy={cy} r="4.5" fill="#ffffff" stroke={ink} strokeWidth="2" />
        ))}
        <path d="M118 26v68" fill="none" stroke={soft} strokeWidth="6" strokeLinecap="round" />
        <path d="M114 26h8M114 94h8" fill="none" stroke={ink} strokeWidth="2.5" strokeLinecap="round" />
    </>
);

const ArthrosisArt = ({ accent, soft, yellow }) => (
    <>
        <path d="M66 34c5 10 9 18 9 29 0 8-2 15-6 24" fill="none" stroke={accent} strokeWidth="6" strokeLinecap="round" />
        <path d="M112 34c-5 10-9 18-9 29 0 8 2 15 6 24" fill="none" stroke={accent} strokeWidth="6" strokeLinecap="round" />
        <circle cx="89" cy="68" r="18" fill={soft} stroke={accent} strokeWidth="3" />
        <circle cx="89" cy="68" r="9" fill="#ffffff" stroke={yellow} strokeWidth="3" />
        <PainSpark x="118" y="66" color={yellow} />
    </>
);

const LegLengthArt = ({ accent, soft, ink }) => (
    <>
        <path d="M66 34v40l-7 20" fill="none" stroke={accent} strokeWidth="7" strokeLinecap="round" />
        <path d="M98 34v47l-7 13" fill="none" stroke={accent} strokeWidth="7" strokeLinecap="round" />
        <path d="M121 28v60" fill="none" stroke={ink} strokeWidth="3" strokeLinecap="round" />
        <path d="M116 34h10M116 46h10M116 58h10M116 70h10M116 82h10" fill="none" stroke={soft} strokeWidth="4" strokeLinecap="round" />
        <path d="M53 96h50" fill="none" stroke={soft} strokeWidth="5" strokeLinecap="round" />
    </>
);

const DeformitiesArt = ({ accent, soft, yellow }) => (
    <>
        <InsoleShape x="72" y="30" fill={soft} stroke={accent} />
        <InsoleShape x="108" y="30" mirror fill={soft} stroke={accent} />
        <path d="M58 86c10-3 18-8 24-16" fill="none" stroke={yellow} strokeWidth="3.5" strokeLinecap="round" />
        <path d="M122 86c-10-3-18-8-24-16" fill="none" stroke={yellow} strokeWidth="3.5" strokeLinecap="round" />
        <path d="M77 67l5 3-2-6" fill="none" stroke={yellow} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M103 67l-5 3 2-6" fill="none" stroke={yellow} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </>
);

const PostureArt = ({ accent, soft, green, ink }) => (
    <>
        <circle cx="72" cy="36" r="10" fill={soft} stroke={accent} strokeWidth="3" />
        <path d="M72 46v20l-10 14" fill="none" stroke={accent} strokeWidth="6" strokeLinecap="round" />
        <path d="M72 56l18 7" fill="none" stroke={accent} strokeWidth="6" strokeLinecap="round" />
        <path d="M62 80l14 18" fill="none" stroke={accent} strokeWidth="6" strokeLinecap="round" />
        <path d="M112 28v64" fill="none" stroke={green} strokeWidth="4" strokeLinecap="round" />
        <path d="M99 28h26" fill="none" stroke={ink} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="5 4" />
    </>
);

const FatigueArt = ({ accent, soft, yellow, ink }) => (
    <>
        <path d="M76 30c6 12 11 22 11 35 0 11-3 18-8 29" fill="none" stroke={accent} strokeWidth="7" strokeLinecap="round" />
        <path d="M95 30c-5 9-7 17-7 25 0 7 2 13 6 20" fill="none" stroke={soft} strokeWidth="9" strokeLinecap="round" />
        <rect x="106" y="50" width="30" height="16" rx="5" fill="#ffffff" stroke={ink} strokeWidth="3" />
        <path d="M136 55h4v6h-4" fill="none" stroke={ink} strokeWidth="3" strokeLinejoin="round" />
        <rect x="110" y="54" width="7" height="8" rx="2" fill={yellow} />
        <path d="M54 58h10M50 68h13M55 78h9" fill="none" stroke={yellow} strokeWidth="3" strokeLinecap="round" />
    </>
);

const KneePainArt = ({ accent, soft, red }) => (
    <>
        <path d="M68 34c7 10 12 20 12 31 0 7-2 13-6 21" fill="none" stroke={accent} strokeWidth="7" strokeLinecap="round" />
        <path d="M104 34c-5 8-7 15-7 24 0 9 3 16 8 26" fill="none" stroke={accent} strokeWidth="7" strokeLinecap="round" />
        <circle cx="90" cy="68" r="16" fill={soft} stroke={accent} strokeWidth="3" />
        <PainSpark x="118" y="62" color={red} />
    </>
);

const XOArt = ({ accent, soft, yellow }) => (
    <>
        <path d="M64 32c9 12 15 24 18 36 3 11 1 19-5 28" fill="none" stroke={accent} strokeWidth="7" strokeLinecap="round" />
        <path d="M116 32c-9 12-15 24-18 36-3 11-1 19 5 28" fill="none" stroke={accent} strokeWidth="7" strokeLinecap="round" />
        <circle cx="58" cy="42" r="12" fill={soft} />
        <text x="58" y="46" textAnchor="middle" fontSize="15" fontWeight="700" fill={accent}>X</text>
        <circle cx="122" cy="42" r="12" fill="#ffffff" stroke={yellow} strokeWidth="3" />
        <text x="122" y="46" textAnchor="middle" fontSize="15" fontWeight="700" fill={yellow}>O</text>
    </>
);

const StandardArt = ({ accent, soft, ink }) => (
    <>
        {/* "Stelka" (o'ngga qaragan arrow) - standard blok orqa ikonasi */}
        <path
            d="M56 64h44"
            fill="none"
            stroke={accent}
            strokeWidth="7"
            strokeLinecap="round"
        />
        <path
            d="M92 44l28 20-28 20"
            fill="none"
            stroke={accent}
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M50 90h90" fill="none" stroke={ink} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="5 4" opacity="0.9" />
    </>
);

const IndividualArt = ({ accent, soft, green }) => (
    <>
        <InsoleShape x="74" y="30" fill={soft} stroke={accent} />
        <path d="M108 38c0-7 5-12 12-12s12 5 12 12v9c0 10-7 17-12 22-5-5-12-12-12-22v-9Z" fill="#ffffff" stroke={green} strokeWidth="3" strokeLinejoin="round" />
        <path d="M120 34c-4 2-6 5-6 9 0 5 4 8 6 11 2-3 6-6 6-11 0-4-2-7-6-9Z" fill={soft} />
        <path d="M114 56c4-1 8-1 12 0M114 64c4-1 8-1 12 0" fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" />
    </>
);

const artByVariant = {
    support: SupportArt,
    flat_feet: FlatFeetArt,
    heel_spur: HeelSpurArt,
    diabetic_ulcers: DiabeticUlcersArt,
    scoliosis: ScoliosisArt,
    arthrosis: ArthrosisArt,
    leg_length_discrepancy: LegLengthArt,
    foot_joint_deformities: DeformitiesArt,
    posture_disorder: PostureArt,
    leg_fatigue: FatigueArt,
    knee_pain: KneePainArt,
    x_o_deformity: XOArt,
    standard: StandardArt,
    individual: IndividualArt
};

const InsoleIllustration = ({ variant, tone = 'primary' }) => {
    const Art = artByVariant[variant] || SupportArt;
    const palette = {
        ...(tones[tone] || tones.primary),
        ...colorsBase
    };

    return <Frame><Art {...palette} /></Frame>;
};

export default InsoleIllustration;
